<script lang="ts" setup>
import { ref, usePromotionConfig, useCoreConfig, useFormat, useDayjs, watch, onBeforeUnmount, computed, useElementVisibility } from '#imports'
import { MessageType, type MessagesComponents as components } from '@core/types'

export interface PromotionUserNotificationCardProps {
    notification: components['schemas']['RenderedNotification']
    view?: 'card' | 'list'
    errors: any
    icon: string
}

const props = withDefaults(defineProps<PromotionUserNotificationCardProps>(), {
    view: 'card'
})

const emit = defineEmits([
    'markAsRead',
    'callAction',
])

const { translations } = usePromotionConfig()
const { translations: coreTranslations } = useCoreConfig()
const dayjs = useDayjs()
const { asRelativeTime } = useFormat()

const loading = ref(false)

const card = ref(null)
const cardIsVisible = useElementVisibility(card, { threshold: 0.66 })
const notificationTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const hasStartedTimer = ref(false)

const hasError = computed(() => props.notification.id in (props?.errors?.value ?? {}))
const timeAgo = asRelativeTime(props.notification.displayed_created_at ?? props.notification.created_at, { timeFormat: 'short', length: 1 })

const expiredAt = asRelativeTime(props.notification.expires_at, { timeFormat: 'long', length: 1 })

const isExpired = computed(() => {
    if (!props.notification.expires_at) return false

    return dayjs.tz().isAfter(dayjs(props.notification.expires_at).tz())
})

const hasActions = computed(() => !!props.notification?.dismissAction || !!props.notification?.acceptAction)

const singleActionText: string | null = props.notification?.dismissAction?.text || props.notification?.acceptAction?.text || null
const singleAction: components['schemas']['RenderedNotificationAction'] | null = props.notification?.dismissAction || props.notification?.acceptAction || null

const onClick = async (action: components['schemas']['RenderedNotificationAction'], notification: components['schemas']['RenderedNotification']) => {
    loading.value = true

    if (!action) {
        loading.value = false

        return
    }

    emit('callAction', { action, notification })

    loading.value = false
}

function startTimer() {
    // Prevent starting multiple timers for the same notification
    if (hasStartedTimer.value || notificationTimer.value) {
        return
    }

    hasStartedTimer.value = true
    notificationTimer.value = setTimeout(() => {
        emit('markAsRead')
        notificationTimer.value = null
    }, 1000)
}

function stopTimer() {
    if (notificationTimer.value) {
        clearTimeout(notificationTimer.value)
        notificationTimer.value = null
    }
}

// Some icons need to be pushed down and left in the icon-box because of visual off-balance
// Add more when necessary
const iconPlacementOffset = MessageType.game_launch as string === props.notification.type

let stopWatch: (() => void) | null = null

if (import.meta.client) {
    stopWatch = watch(cardIsVisible, (isVisible) => {
        if (isVisible && !props.notification.read) {
            startTimer()
        }
        else {
            stopTimer()
        }
    }, { immediate: true })
}

onBeforeUnmount(() => {
    stopTimer()
    if (stopWatch) stopWatch()
})
</script>

<template>
    <article
        ref="card"
        class="notification-card"
        :class="{ 'notification-card--list': props.view === 'list', 'notification-card--card': props.view === 'card', 'notification-card--disabled': isExpired, 'notification-card--no-actions': !hasActions }"
    >
        <div class="notification-card__content content">
            <div v-if="!props.notification.read && props.view === 'card'" class="content__unread" />
            <div v-if="isExpired && props.notification.expires_at" class="content__expired">
                {{ translations.notifications.expired }} - {{ expiredAt }}
            </div>
            <div v-if="props.view === 'card'" class="content__delivered-at">
                <Transition name="fade" mode="out-in">
                    <span v-if="timeAgo">
                        {{ timeAgo }}
                    </span>
                    <span v-else class="time-skeleton" />
                </Transition>
            </div>
            <div class="content__icon" :class="{ 'content__icon--offset': iconPlacementOffset }">
                <div class="content__icon-background">
                    <UIIcon :name="props.icon" size="24" />
                </div>
            </div>
            <div class="content__title">
                <h3 class="use-brand-font">
                    {{ props.notification.title }}
                </h3>
                <Transition name="fade" mode="out-in">
                    <span v-if="timeAgo && props.view === 'list'" class="content__delivered-at">
                        <span v-if="!props.notification.read" class="content__unread" />
                        {{ timeAgo }}
                    </span>
                    <span v-else-if="props.view === 'list'" class="time-skeleton" />
                </Transition>
            </div>
            <div class="content__message">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <p v-html="props.notification.body" />
            </div>
            <Transition
                name="fade"
                mode="out-in"
                appear
            >
                <UIAlert
                    v-if="hasError"
                    :message="coreTranslations.defaultErrorMessage"
                    class="content__error"
                    :duration="5000"
                />
            </Transition>
            <div
                class="content__actions"
            >
                <template v-if="!!props.notification?.dismissAction && !!props.notification?.acceptAction">
                    <UIButton as="cta" size="sm" @click="onClick(props.notification.acceptAction, props.notification)">
                        {{ props.notification.acceptAction.text }}
                    </UIButton>
                    <UIButton
                        color="secondary"
                        outlined
                        size="sm"
                        @click="onClick(props.notification.dismissAction, props.notification)"
                    >
                        {{ props.notification.dismissAction.text }}
                    </UIButton>
                </template>
                <UIButton
                    v-else-if="singleAction && singleActionText"
                    color="secondary"
                    size="sm"
                    @click="onClick(singleAction, props.notification)"
                >
                    {{ singleActionText }}
                </UIButton>
            </div>
        </div>

        <Transition
            name="fade"
            appear
        >
            <div
                v-if="loading"
                class="notification-card-overlay"
            >
                <UILoader :text="translations.pleaseWait" />
            </div>
        </Transition>
    </article>
</template>

<style lang="scss" scoped>
.notification-card {
    position: relative;
    display: grid;
    width: 100%;
    background-color: var(--notification-card-bg, var(--background));
    border-radius: var(--border-radius);
    overflow: hidden;

    .content {
        display: grid;
        padding: 15px 25px;
        flex-shrink: 0;
        gap: 10px;

        &__unread {
            position: absolute;
            left: 10px;
            width: 8px;
            height: 8px;
            background-color: var(--danger);
            border-radius: 50%;
        }

        &__delivered-at {
            grid-area: delivered-at;
            margin-bottom: -10px;
        }

        &__expired {
            grid-area: expired;
            justify-self: start;
            font-size: $font-size-xs;
            padding: 5px 8px;
            margin-bottom: 8px;
            color: var(--on-warning);
            background-color: var(--warning);
            border: 1px solid;
            border-color: var(--border-warning-light, color.adjust($brand-warning-border, $lightness: -5%));
            border-radius: var(--border-radius);
        }

        &__icon {
            grid-area: icon;
            width: 45px;
            aspect-ratio: 1;
            color: var(--notification-card-icon-color, var(--secondary));

            &-background {
                display: flex;
                justify-content: center;
                align-items: center;
                width: 45px;
                height: 45px;
                border-radius: 50%;
                color: inherit;
                background-color: var(--notification-card-icon-bg, var(--surface));
            }

            :deep(.icon) {
                width: 100%;
                height: 100%;
                max-width: 24px;
                max-height: 24px;
            }

            &--offset {
                .icon {
                    margin-top: 2px;
                    margin-right: 2px;
                }
            }
        }

        &__title {
            grid-area: title;

            h3 {
                font-size: $font-size-base;
                font-weight: $text-medium;
            }
        }

        &__message {
            grid-area: message;
            color: var(--on-background);

            p {
                font-size: $font-size-sm;
            }
        }

        .time-skeleton {
            display: block;
            width: 75px;
            background-color: rgb(0 0 0 / 15%);
            border-radius: var(--border-radius);
            height: 1em;
            animation: pulse-opacity 3s ease-in-out forwards;
        }

        &__error {
            grid-area: error;
            align-self: flex-end;
            margin-bottom: 0 !important;
            z-index: 1;
        }

        &__actions {
            grid-area: actions;
            margin-top: auto; // Push to bottom
            align-self: end;
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            width: 100%;
            gap: 5px;

            button {
                flex: 1;
            }
        }
    }

    &--list .content {
        grid-template-areas:
            'expired expired expired expired'
            'icon title title title'
            'icon message message actions'
            'icon message message actions';
        grid-template-columns: 28px 1fr 1fr auto;
        gap: 0px 15px;
        align-items: start;
        padding: 15px 25px 15px 15px;

        .content__delivered-at {
            font-size: $font-size-xxs;
            color: $notification-time-stamp-color;
        }

        .content__delivered-at {
            position: relative;
            align-self: flex-start;
            height: 12px;
            margin-left: 20px;
            margin-top: 0px;
            margin-bottom: 8px;
            white-space: nowrap;
            grid-area: unset;
        }

        .content__message p {
            font-size: $font-size-xs;
            margin-bottom: 0;
            margin-top: 0;
        }

        .content__title {
            display: inline-flex;
            margin-right: 20px;
            position: relative;

            h3 {
                font-size: $font-size-xs + 1;
                margin-bottom: 0;
                margin-top: 0;
            }

            .content__unread {
                position: absolute;
                left: -12px;
                top: 2px;
            }
        }

        .content__icon {
            width: 28px;
            margin-top: -7px;

            &-background {
                width: 28px;
                height: 28px;
                background-color: transparent;
            }

            :deep(.icon) {
                max-height: 18px;
            }
        }

        .content__actions {
            margin-top: unset;
            align-self: center;
            flex-direction: column;
        }
    }

    &--card .content {
        grid-template-areas:
            'delivered-at delivered-at delivered-at'
            'title title icon'
            'message message message'
            'error error error'
            'expired expired expired'
            'actions actions actions';

        grid-template-columns: 1fr 1fr 45px;

        .content__unread {
            margin-top: 4px;
        }

        .content__delivered-at {
            height: $font-size-base;
            font-size: $font-size-xs;
            color: $notification-time-stamp-color;
        }
    }

    &-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: $border-radius;
        background: rgba(255, 255, 255, 0.75);
        backdrop-filter: blur(3px);
        -webkit-backdrop-filter: blur(3px);
        z-index: 13;
    }

    &--no-actions {
        .content--list {
            grid-template-areas:
                'expired expired expired expired'
                'icon title title title'
                'icon message message message';
            grid-template-columns: 28px 1fr 1fr 1fr;
        }
    }

    &--disabled {
        user-select: none;
        pointer-events: none;

        .content {
            &__title,
            &__message,
            &__actions {
                opacity: 0.5;
            }
        }
    }
}

@keyframes pulse-opacity {
    0% {
        opacity: 0;
    }

    16.66% {
        opacity: 1;
    }

    33.33% {
        opacity: 0;
    }

    50% {
        opacity: 1;
    }

    66.66% {
        opacity: 0;
    }

    83.33% {
        opacity: 1;
    }

    100% {
        opacity: 0;
    }
}
</style>
